import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Polygon, Circle, useMap, useMapEvents, ZoomControl } from "react-leaflet";
import L from "leaflet";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import AppHeader from "@/components/AppHeader";
import GeoSearchAutocomplete from "@/components/GeoSearchAutocomplete";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useGuestGate } from "@/components/GuestGate";
import { Plus, Filter as FilterIcon, Layers, Locate, LocateFixed, AlertTriangle, RefreshCw, ChevronRight, ChevronLeft, MessageCircle, Map as MapIconLucide, Satellite, ChevronDown, ChevronUp, X, RotateCcw, List as ListIcon, Tag, Gift, Recycle, Leaf, Search } from "lucide-react";
import { markerColor, pinIcon, TileHealth, RISK_COLORS, STATUS_GRAY, STATUS_BLUE, STATUS_PURPLE } from "@/components/MapCore";
import MarkerClusterGroup from "@/components/MarkerClusterGroup";
import MarkerPreviewCard from "@/components/MarkerPreviewCard";
import { useAssistantPageTips } from "@/components/ContextAssistant";
import { toast } from "sonner";
import { labelCat, labelSub, labelItem } from "@/lib/marketplaceLabels";

// Haversine distance in kilometers between two [lat, lng] points.
const haversineKm = (a, b) => {
  if (!a || !b) return Infinity;
  const R = 6371, toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLng = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const SUBS_BY_CAT = {
  waste: ["small_pile","bags","plastic","glass","paper","organic","tires","appliances","furniture","construction","industrial","hazardous","asbestos","chemicals","oil","batteries","illegal_dump","marine","river","forest","sewage_spill","medical_waste","explosive_flammable"],
  environment: ["water_pollution","soil_pollution","suspicious_discharge","smoke_emissions","fire","fire_damage","damaged_trees","degraded_green","water_leak","erosion","contamination","beach_coast_degradation"],
  animals: ["injured","abandoned","trapped","in_danger","suspected_mistreatment","poor_conditions","wildlife_distress"],
  urban: ["broken_light","broken_bench","damaged_playground","pothole","architectural_barrier","dangerous_sidewalk","broken_signage","abandoned_public","vandalism","other"],
  vegetation: ["tall_grass","brambles","dangerous_branches","obstructing_visibility","fallen_tree","invasive_plants","road_encroachment","other"],
  reusable: ["gift","bed","mattress","table","chair","wardrobe","sofa","furniture","home_decor","appliance","electronics","computer","bicycle","tools","clothing","toys","construction_materials","books","other"],
};

function LocateButton({ onDeny, t }) {
  const map = useMap();
  return (
    <button
      data-testid="map-locate-btn"
      onClick={() => {
        if (!navigator.geolocation) { onDeny && onDeny(t("map_locate_unsupported")); return; }
        if (localStorage.getItem("tc_geo_enabled") === "false") { onDeny && onDeny(t("map_geo_disabled")); return; }
        navigator.geolocation.getCurrentPosition(
          (pos) => { map.flyTo([pos.coords.latitude, pos.coords.longitude], 15, { duration: 1.2 }); },
          (err) => { onDeny && onDeny(err.code === 1 ? t("map_geo_denied") : t("map_geo_failed")); }
        );
      }}
      className="absolute right-4 z-[1000] w-11 h-11 rounded-full glass-panel flex items-center justify-center hover:bg-white bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:bottom-[calc(6rem+env(safe-area-inset-bottom,0px))]"
      title={t("map_locate_title")}
    >
      <Locate size={18} className="text-slate-800" />
    </button>
  );
}

function ZoomTracker({ onChange }) {
  const map = useMapEvents({ zoomend: () => onChange(map.getZoom()) });
  useEffect(() => { onChange(map.getZoom()); }, []); // eslint-disable-line
  return null;
}

export default function MapHub() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const { openGate } = useGuestGate();
  useAssistantPageTips([
    { id: "map-colors", text: t("tip_map_colors") },
    { id: "map-filters", text: t("tip_map_filters") },
    { id: "map-duplicates", text: t("tip_map_duplicates") },
  ]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  // ── Session-scoped filter persistence ─────────────────────────────
  // Uses `sessionStorage` so filters survive:
  //   • navigation to a report/listing detail and back;
  //   • filter panel close/open;
  //   • hard refresh of the page within the same tab.
  // But are automatically discarded when the tab is closed (new session) and
  // are also wiped on login / logout / register / session expiration
  // (see auth.jsx).
  const SESSION_KEY = "tc_map_filters";
  const persisted = (() => {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null") || {}; }
    catch { return {}; }
  })();
  // Older builds used localStorage — clear it once to avoid cross-session leaks.
  try { localStorage.removeItem(SESSION_KEY); } catch {}
  const [filters, setFilters] = useState({
    category: persisted.category || "all",
    subcategory: persisted.subcategory || "all",
    risk_level: persisted.risk_level || "all",
    status: persisted.status || "all",
    area: persisted.area || null,
  });
  const [tileErr, setTileErr] = useState(false);
  const [tileLoaded, setTileLoaded] = useState(false);
  const [geoMsg, setGeoMsg] = useState("");
  const [tileKey, setTileKey] = useState(0);
  const [tileMode, setTileMode] = useState("standard");
  const [filtersOpen, setFiltersOpen] = useState(false); // start closed per spec
  const [fabMinimized, setFabMinimized] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [risks, setRisks] = useState(persisted.risks || []); // multi-select: ["red","yellow","green"]
  const [statuses, setStatuses] = useState(persisted.statuses || []); // multi-select
  // ── Content-type toggles ──────────────────────────────────────────
  const [showReports, setShowReports] = useState(persisted.showReports !== undefined ? !!persisted.showReports : true);
  // Marketplace overlay on the main map ─────────────────────────────
  const [mkTypes, setMkTypes] = useState(new Set(Array.isArray(persisted.mkTypes) ? persisted.mkTypes : []));
  const [mkItems, setMkItems] = useState([]);
  const [mkTaxonomy, setMkTaxonomy] = useState({});
  const [mkCat, setMkCat] = useState(persisted.mkCat || "all");
  const [mkSub, setMkSub] = useState(persisted.mkSub || "all");
  const [mkItem, setMkItem] = useState(persisted.mkItem || "all");
  const [mkCondition, setMkCondition] = useState(persisted.mkCondition || "all");
  const [mkNegotiable, setMkNegotiable] = useState(!!persisted.mkNegotiable);
  const [mkPriceMin, setMkPriceMin] = useState(persisted.mkPriceMin || "");
  const [mkPriceMax, setMkPriceMax] = useState(persisted.mkPriceMax || "");
  // Text search over Marketplace items shown on the map. Same behaviour as the
  // Marketplace feed: matches title/description/keywords/category/subcategory/
  // item_type/province_*. Debounced 300ms client-side; passed as `q` param to
  // GET /marketplace/listings. Persisted temporarily in sessionStorage.
  const [mkQuery, setMkQuery] = useState(persisted.mkQuery || "");
  const [mkQueryDebounced, setMkQueryDebounced] = useState(mkQuery);
  useEffect(() => {
    const t = setTimeout(() => setMkQueryDebounced(mkQuery), mkQuery ? 300 : 0);
    return () => clearTimeout(t);
  }, [mkQuery]);
  const [showChooser, setShowChooser] = useState(false);
  const toggleMkType = (tt) => {
    // Guests cannot enable any Marketplace overlay — show the guest gate
    // instead of triggering a backend request that would return 401.
    if (!user) { openGate({ message: t("guest_gate_marketplace") || "Per vedere questa sezione devi accedere o registrarti." }); return; }
    setMkTypes((s) => {
      const n = new Set(s);
      if (n.has(tt)) n.delete(tt); else n.add(tt);
      return n;
    });
  };
  const [showUrgent, setShowUrgent] = useState(persisted.showUrgent !== undefined ? !!persisted.showUrgent : true);
  const [urgentAlerts, setUrgentAlerts] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(6);
  const [listOpen, setListOpen] = useState(false);
  // ── Proximity filter ("Vicino a me") ───────────────────────────────
  // Kept independent from the manual province search so users can still
  // freely explore any area with the filter disabled.
  const [meLoc, setMeLoc] = useState(persisted.meLoc || null);
  const [proxOn, setProxOn] = useState(!!persisted.proxOn);
  const [radiusKm, setRadiusKm] = useState(persisted.radiusKm || 25);
  const [geoBusy, setGeoBusy] = useState(false);

  const askProximity = () => {
    if (proxOn) { setProxOn(false); return; }
    if (!navigator.geolocation) { toast.error(t("map_locate_unsupported") || "Geolocalizzazione non supportata"); return; }
    if (localStorage.getItem("tc_geo_enabled") === "false") { toast.error(t("map_geo_disabled") || "Geolocalizzazione disattivata nelle impostazioni"); return; }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => { setMeLoc([p.coords.latitude, p.coords.longitude]); setProxOn(true); setGeoBusy(false); },
      (err) => { setGeoBusy(false); toast.error(err.code === 1 ? (t("map_geo_denied") || "Permesso posizione negato") : (t("map_geo_failed") || "Impossibile ottenere la posizione")); },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 }
    );
  };

  // Persist the full filter state in sessionStorage so it survives
  // navigation and refresh within the same tab. The key is wiped on
  // logout/login/register/session-expiry (see auth.jsx) so filters never
  // cross sessions or accounts.
  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({
        ...filters,
        risks, statuses,
        showReports, showUrgent,
        mkTypes: Array.from(mkTypes),
        mkCat, mkSub, mkItem, mkCondition, mkNegotiable,
        mkPriceMin, mkPriceMax, mkQuery,
        proxOn, radiusKm, meLoc,
      }));
    } catch {}
  }, [filters, risks, statuses, showReports, showUrgent, mkTypes, mkCat, mkSub, mkItem, mkCondition, mkNegotiable, mkPriceMin, mkPriceMax, mkQuery, proxOn, radiusKm, meLoc]);

  const toggleInArr = (arr, val, setter) => setter(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  const clearAll = () => {
    // "Azzera" wipes concrete filter VALUES from the session but preserves
    // the user's Tipologia selection (iter39 rule: don't force-reactivate
    // Segnalazioni ambientali). The session key itself is updated by the
    // persistence effect right after.
    setFilters({ category: "all", subcategory: "all", risk_level: "all", status: "all", area: null });
    setRisks([]); setStatuses([]);
    setProxOn(false);
    setMkCat("all"); setMkSub("all"); setMkItem("all");
    setMkCondition("all"); setMkNegotiable(false);
    setMkPriceMin(""); setMkPriceMax("");
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
  };
  const showAll = () => { setRisks(["red","yellow","green"]); setStatuses(["published","in_progress","in_review","standby","resolved","reopened"]); };

  useEffect(() => {
    if (!showUrgent) return;
    let cancelled = false;
    const buildParams = () => {
      const p = {};
      if (filters.area?.name) {
        p.province = filters.area.name;
        if (filters.area.country_code) p.country_code = filters.area.country_code;
      }
      return p;
    };
    api.get("/events", { params: buildParams() })
      .then((r) => { if (!cancelled) setUrgentAlerts(r.data || []); })
      .catch(() => {});
    const iv = setInterval(() => {
      api.get("/events", { params: buildParams() })
        .then((r) => { if (!cancelled) setUrgentAlerts(r.data || []); })
        .catch(() => {});
    }, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [showUrgent, filters.area]);

  useEffect(() => {
    setLoading(true);
    const q = {};
    // Virtual reusable buckets: 3 distinct categories in the UI, mapped to
    // (category=reusable + subcategory) tuples for the backend.
    const virt = filters.category;
    if (virt === "v_gift") { q.category = "reusable"; q.subcategory = "gift"; }
    else if (virt === "v_sale") { q.category = "reusable"; q.subcategory = "sale"; }
    else if (virt === "v_reusable_found") { q.category = "reusable"; /* subcat filter applied client-side */ }
    else if (virt !== "all") { q.category = virt; if (filters.subcategory !== "all") q.subcategory = filters.subcategory; }
    else if (filters.subcategory !== "all") q.subcategory = filters.subcategory;
    // Multi-select risks
    if (risks.length > 0) q.risk_level = risks.join(",");
    else if (filters.risk_level !== "all") q.risk_level = filters.risk_level;
    // Multi-select statuses
    if (statuses.length > 0) q.status = statuses.join(",");
    else if (filters.status !== "all") q.status = filters.status;
    // Province (area) filter — map, list and counter must all reflect it.
    if (filters.area?.name) {
      q.province = filters.area.name;
      if (filters.area.country_code) q.country_code = filters.area.country_code;
    }
    api.get("/reports", { params: q })
      .then((r) => {
        let data = r.data;
        if (virt === "v_reusable_found") {
          data = data.filter((rep) => rep.subcategory !== "gift" && rep.subcategory !== "sale");
        }
        setReports(data);
      })
      .finally(() => setLoading(false));
  }, [filters, risks, statuses]);

  useEffect(() => { setFilters((f) => ({ ...f, subcategory: "all" })); /* eslint-disable-next-line */ }, [filters.category]);

  // Load marketplace taxonomy once for authenticated users only.
  useEffect(() => {
    if (!user) { setMkTaxonomy({}); return; }
    api.get("/marketplace/taxonomy").then((r) => setMkTaxonomy(r.data || {})).catch(() => {});
  }, [user]);
  // Reset dependent marketplace fields when the parent changes.
  useEffect(() => { setMkSub("all"); setMkItem("all"); }, [mkCat]);
  useEffect(() => { setMkItem("all"); }, [mkSub]);

  // Fetch marketplace listings when any relevant filter changes. Cat/sub/item
  // + price range are sent to the backend; condition/negotiable are applied
  // client-side inside visibleMkItems.
  useEffect(() => {
    if (mkTypes.size === 0 || !user) { setMkItems([]); return; }
    let alive = true;
    (async () => {
      const results = [];
      for (const tp of mkTypes) {
        const params = { type: tp, limit: 500 };
        if (mkCat !== "all") params.category = mkCat;
        if (mkSub !== "all") params.subcategory = mkSub;
        if (mkItem !== "all") params.item_type = mkItem;
        if (mkQueryDebounced && mkQueryDebounced.trim()) params.q = mkQueryDebounced.trim();
        // Lotto 4 #14 — forward the area filter so the marketplace slice of
        // the counter matches marker/list. Without this, the counter used to
        // include listings outside the selected province, breaking the promise
        // "contatore = risultati effettivi = marker/elenco coerenti".
        if (filters.area?.name) {
          params.province = filters.area.name;
          if (filters.area.country_code) params.country_code = filters.area.country_code;
        }
        if (tp === "sale") {
          let pMin = mkPriceMin && !isNaN(Number(mkPriceMin)) ? Number(mkPriceMin) : null;
          let pMax = mkPriceMax && !isNaN(Number(mkPriceMax)) ? Number(mkPriceMax) : null;
          if (pMin != null && pMax != null && pMin > pMax) { const tmp = pMin; pMin = pMax; pMax = tmp; }
          if (pMin != null) params.price_min = pMin;
          if (pMax != null) params.price_max = pMax;
        }
        try {
          const { data } = await api.get("/marketplace/listings", { params });
          results.push(...(data || []));
        } catch {}
      }
      if (alive) setMkItems(results);
    })();
    return () => { alive = false; };
  }, [user, mkTypes, mkCat, mkSub, mkItem, mkPriceMin, mkPriceMax, mkQueryDebounced, filters.area]);

  const center = useMemo(() => (reports.length ? [reports[0].lat, reports[0].lng] : [41.9028, 12.4964]), [reports]);
  const catLabel = (c) => t(`cat_${c}`) || c;
  const retryTiles = () => { setTileErr(false); setTileLoaded(false); setTileKey((k) => k + 1); };
  const availableSubs = filters.category !== "all" ? (SUBS_BY_CAT[filters.category] || []) : [];

  // Apply proximity filter (client-side) on top of every other filter so that
  // markers, list and counter always stay perfectly in sync.
  const visibleReports = useMemo(() => {
    if (!showReports) return [];
    if (!proxOn || !meLoc) return reports;
    return reports.filter((r) => typeof r.lat === "number" && typeof r.lng === "number" && haversineKm(meLoc, [r.lat, r.lng]) <= radiusKm);
  }, [reports, proxOn, meLoc, radiusKm, showReports]);
  const visibleAlerts = useMemo(() => {
    if (!proxOn || !meLoc) return urgentAlerts;
    return urgentAlerts.filter((e) => {
      const c = e.centroid || { lat: e.lat, lng: e.lng };
      if (typeof c?.lat !== "number" || typeof c?.lng !== "number") return false;
      return haversineKm(meLoc, [c.lat, c.lng]) <= radiusKm;
    });
  }, [urgentAlerts, proxOn, meLoc, radiusKm]);
  const visibleMkItems = useMemo(() => {
    let arr = mkItems.filter((l) => typeof l.lat === "number" && typeof l.lng === "number");
    // Client-side: condition + trattabile — apply only to sale rows.
    if (mkCondition !== "all") arr = arr.filter((l) => l.type !== "sale" || l.condition === mkCondition);
    if (mkNegotiable) arr = arr.filter((l) => l.type !== "sale" || !!l.negotiable);
    // Guard against min > max — swap to keep the intended [min, max] range.
    if (mkPriceMin || mkPriceMax) {
      const a = Number(mkPriceMin), b = Number(mkPriceMax);
      let lo = mkPriceMin && !isNaN(a) ? a : null;
      let hi = mkPriceMax && !isNaN(b) ? b : null;
      if (lo != null && hi != null && lo > hi) { const t = lo; lo = hi; hi = t; }
      arr = arr.filter((l) => {
        if (l.type !== "sale") return true; // gift/reusable are never filtered by price
        if (lo != null && (l.price == null || l.price < lo)) return false;
        if (hi != null && (l.price == null || l.price > hi)) return false;
        return true;
      });
    }
    if (!proxOn || !meLoc) return arr;
    return arr.filter((l) => haversineKm(meLoc, [l.lat, l.lng]) <= radiusKm);
  }, [mkItems, proxOn, meLoc, radiusKm, mkPriceMin, mkPriceMax, mkCondition, mkNegotiable]);

  const buildMarker = useCallback((r) => {
    const marker = L.marker([r.lat, r.lng], { icon: pinIcon(markerColor(r), r.message_count || 0) });
    marker.on("click", () => setSelectedReport(r));
    return marker;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Marker builder for marketplace listings overlaid on the main map. Uses the
  // three canonical brand colors: viola=Vendo, blu=Regalo, marrone=Riutilizzabile.
  const MK_TYPE_COLOR = { sale: "#7C3AED", gift: "#2563EB", reusable: "#8B5E34" };
  const MK_TYPE_LABEL = { sale: t("listing_type_sale"), gift: t("listing_type_gift"), reusable: t("listing_type_reusable") };
  const buildMkMarker = useCallback((l) => {
    const c = MK_TYPE_COLOR[l.type] || "#334155";
    const m = L.marker([l.lat, l.lng], { icon: new L.DivIcon({
      className: "",
      html: `<div style="width:22px;height:22px;background:${c};border:3px solid #fff;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,.3)"></div>`,
      iconSize: [22, 22], iconAnchor: [11, 11],
    }), tcType: l.type });
    const price = l.type === "sale" ? ` · €${l.price ?? 0}` : "";
    const typeLabel = MK_TYPE_LABEL[l.type] || l.type;
    m.bindPopup(`<div style="font-size:12px"><b>${(l.title || "").replace(/</g, "&lt;")}</b><br>${typeLabel}${price}<br><a href="/marketplace/${l.listing_id}" style="color:#047857">${t("details_arrow")}</a></div>`);
    m.on("click", () => m.openPopup());
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, lang]);

  // Cluster palette for the Marketplace overlay. Colors follow the brand
  // taxonomy: viola=Vendo, blu=Regalo, marrone=Riutilizzabile trovato.
  // A mixed cluster (multiple types) uses a neutral slate palette so it's
  // clear that it contains different content; zooming in restores the exact
  // per-marker colors.
  const MK_CLUSTER = {
    sale:     { from: "#A78BFA", to: "#5B21B6", shadow: "rgba(91,33,182,.35)" },
    gift:     { from: "#60A5FA", to: "#1E40AF", shadow: "rgba(30,64,175,.35)" },
    reusable: { from: "#C08E5C", to: "#5C3A1E", shadow: "rgba(92,58,30,.35)" },
    mixed:    { from: "#94A3B8", to: "#334155", shadow: "rgba(51,65,85,.35)" },
  };
  const mkClusterPalette = useCallback((children) => {
    const types = new Set();
    for (const c of children) {
      const tt = c?.options?.tcType;
      if (tt) types.add(tt);
    }
    if (types.size === 0) return MK_CLUSTER.mixed;
    if (types.size === 1) return MK_CLUSTER[[...types][0]] || MK_CLUSTER.mixed;
    return MK_CLUSTER.mixed;
  }, []);

  return (
    <div className="h-[100dvh] flex flex-col" data-testid="map-hub">
      <AppHeader />
      {/* Lotto 6 #5 — 1px emerald border on all four sides of the map.
          The outer padding gives just enough room to see the border on
          every edge (top/right/bottom/left) without changing the map's
          usable dimensions in a perceivable way. Rounded corners are
          gentle (rounded-2xl = 16px). No shadow, no contrasting fill. */}
      <div className="flex-1 min-h-[400px] p-2 sm:p-3 flex">
        <div
          className="relative flex-1 border border-emerald-500/30 rounded-2xl overflow-hidden"
          data-testid="leaflet-map"
        >
          <MapContainer
          key={`map-${tileKey}`}
          center={center}
          zoom={reports.length ? 5 : 4}
          scrollWheelZoom={true}
          zoomControl={false}
          style={{ width: "100%", height: "100%" }}
        >
          <ZoomControl position="bottomleft" />
          <TileHealth
            key={`tiles-${tileKey}-${tileMode}`}
            mode={tileMode}
            onLoad={() => setTileLoaded(true)}
            onError={() => setTileErr(true)}
          />
          <MarkerClusterGroup reports={visibleReports} buildMarker={buildMarker} />
          {mkTypes.size > 0 && (
            <MarkerClusterGroup
              reports={visibleMkItems.map((l) => ({ ...l, report_id: l.listing_id }))}
              buildMarker={buildMkMarker}
              clusterPalette={mkClusterPalette}
            />
          )}
          <ZoomTracker onChange={setZoomLevel} />
          {proxOn && meLoc && (
            <Circle
              center={meLoc}
              radius={radiusKm * 1000}
              pathOptions={{ color: "#059669", weight: 1.5, fillColor: "#10B981", fillOpacity: 0.06 }}
            />
          )}
          {showUrgent && visibleAlerts.map((e) => {
            const centroid = e.centroid || { lat: e.lat, lng: e.lng };
            const coords = ((e.geometry?.coordinates) || [[]])[0];
            const polyLL = coords.map(([lng, lat]) => [lat, lng]);
            // Adaptive: show polygon only when the event's approximate area is
            // comparable to the visible viewport. Formula: show when
            // zoomLevel >= threshold(area_km2). Fallback: zoom >= 12 for small
            // events, zoom >= 9 for medium, zoom >= 7 for large ones.
            const area = e.area_km2 || 0;
            let polyZoom = 12;
            if (area >= 1000) polyZoom = 6;
            else if (area >= 100) polyZoom = 7;
            else if (area >= 10) polyZoom = 9;
            else if (area >= 1) polyZoom = 11;
            const showPoly = polyLL.length >= 3 && zoomLevel >= polyZoom;
            return (
              <React.Fragment key={e.event_id}>
                <Marker
                  position={[centroid.lat || 0, centroid.lng || 0]}
                  icon={new L.DivIcon({
                    className: "",
                    html: `<div style="width:28px;height:28px;background:#EF4444;border:3px solid #fff;border-radius:50%;box-shadow:0 4px 10px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:14px">!</div>`,
                    iconSize: [28, 28], iconAnchor: [14, 14],
                  })}
                  eventHandlers={{ click: () => nav(`/events/${e.event_id}`) }}
                />
                {showPoly && (
                  <Polygon
                    positions={polyLL}
                    pathOptions={{ color: "#EF4444", weight: 2, fillColor: "#EF4444", fillOpacity: 0.15 }}
                    eventHandlers={{ click: () => nav(`/events/${e.event_id}`) }}
                  />
                )}
              </React.Fragment>
            );
          })}
          <LocateButton onDeny={(m) => setGeoMsg(m)} t={t} />
        </MapContainer>

        <MarkerPreviewCard report={selectedReport} onClose={() => setSelectedReport(null)} />

        {/* Publication chooser: forces the user to pick between an environmental
            report and one of the three marketplace flows before entering the form. */}
        <Dialog open={showChooser} onOpenChange={setShowChooser}>
          <DialogContent className="max-w-lg" data-testid="publish-chooser">
            <DialogHeader>
              <DialogTitle className="text-2xl">{t("publish_chooser_title")}</DialogTitle>
              <DialogDescription>{t("publish_chooser_desc")}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
              {[
                { k: "report", color: "#059669", icon: Leaf, title: t("publish_chooser_report_title"), desc: t("publish_chooser_report_desc"), go: "/report/new", tid: "chooser-report" },
                { k: "sale", color: "#7C3AED", icon: Tag, title: t("publish_chooser_sale_title"), desc: t("publish_chooser_sale_desc"), go: "/marketplace/new?type=sale", tid: "chooser-sale" },
                { k: "gift", color: "#2563EB", icon: Gift, title: t("publish_chooser_gift_title"), desc: t("publish_chooser_gift_desc"), go: "/marketplace/new?type=gift", tid: "chooser-gift" },
                { k: "reusable", color: "#8B5E34", icon: Recycle, title: t("publish_chooser_reusable_title"), desc: t("publish_chooser_reusable_desc"), go: "/marketplace/new?type=reusable", tid: "chooser-reusable" },
              ].map((c) => {
                const Icon = c.icon;
                return (
                  <button
                    key={c.k}
                    data-testid={c.tid}
                    onClick={() => { setShowChooser(false); nav(c.go); }}
                    className="text-left rounded-xl border-2 p-3 hover:shadow-md transition-shadow"
                    style={{ borderColor: c.color }}
                  >
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white mb-2" style={{ background: c.color }}>
                      <Icon size={18} />
                    </div>
                    <div className="text-sm font-bold text-slate-900 mb-0.5">{c.title}</div>
                    <div className="text-[11px] text-slate-600 leading-snug">{c.desc}</div>
                  </button>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>

        {/* Filters toggle (always visible, top-left) */}
        <button
          data-testid="filters-toggle"
          onClick={() => setFiltersOpen((o) => !o)}
          className="absolute top-4 left-4 z-[1001] h-10 rounded-full glass-panel px-4 flex items-center gap-2 font-semibold text-sm text-slate-800 hover:bg-white"
          title={filtersOpen ? t("map_hide_filters") : t("map_show_filters")}
        >
          <FilterIcon size={14} className="text-emerald-700" />
          <span className="hidden sm:inline">{filtersOpen ? t("map_hide_filters") : t("map_show_filters")}</span>
          {filtersOpen ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>

        {/* Tile mode switcher */}
        <div className="absolute top-4 right-4 z-[1000] glass-panel rounded-full p-1 flex fade-in" data-testid="tile-mode-switch">
          <button data-testid="tile-mode-standard" onClick={() => { setTileMode("standard"); setTileErr(false); }} className={`h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${tileMode === "standard" ? "bg-slate-900 text-white" : "text-slate-700 hover:text-slate-900"}`}>
            <MapIconLucide size={13} /> <span className="hidden sm:inline">{t("map_std")}</span>
          </button>
          <button data-testid="tile-mode-satellite" onClick={() => { setTileMode("satellite"); setTileErr(false); }} className={`h-9 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${tileMode === "satellite" ? "bg-slate-900 text-white" : "text-slate-700 hover:text-slate-900"}`}>
            <Satellite size={13} /> <span className="hidden sm:inline">{t("map_satellite")}</span>
          </button>
        </div>

        {tileErr && !tileLoaded && (
          <div className="absolute inset-x-0 top-4 mx-auto max-w-md z-[1200] glass-panel rounded-2xl p-4 fade-in" data-testid="map-error-banner">
            <div className="flex items-start gap-3">
              <AlertTriangle size={18} className="text-red-600 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">{t("map_tiles_error")}</div>
                <div className="text-xs text-slate-600 mt-1">{t("map_tiles_check")}</div>
              </div>
              <Button data-testid="map-retry-btn" onClick={retryTiles} size="sm" className="rounded-full bg-slate-900 hover:bg-slate-800 hover:text-white text-white">
                <RefreshCw size={12} className="mr-1" /> {t("map_retry")}
              </Button>
            </div>
          </div>
        )}

        {geoMsg && (
          <div className="absolute inset-x-0 top-4 mx-auto max-w-md z-[1200] glass-panel rounded-2xl p-3 fade-in" data-testid="geo-msg">
            <div className="flex items-center gap-2 text-sm text-slate-800">
              <AlertTriangle size={14} className="text-yellow-600 shrink-0" />
              <span className="flex-1">{geoMsg}</span>
              <button onClick={() => setGeoMsg("")} className="text-slate-500 hover:text-slate-900 text-xs">{t("close")}</button>
            </div>
          </div>
        )}

        {/* Filters panel — compact mobile-first, sticky header + footer, internal scroll */}
        {filtersOpen && (
          <>
            {/* Backdrop for outside-click close on mobile */}
            <div
              data-testid="filters-backdrop"
              onClick={() => setFiltersOpen(false)}
              className="absolute inset-0 z-[999] bg-black/10 sm:hidden"
              aria-hidden
            />
            <div
              className="absolute top-16 left-2 right-2 sm:left-4 sm:right-auto z-[1000] glass-panel rounded-2xl sm:w-[300px] flex flex-col fade-in"
              style={{ maxHeight: "min(72vh, calc(100vh - 120px))" }}
              data-testid="map-filters"
            >
              {/* Sticky header */}
              <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-200/70 rounded-t-2xl bg-white/85 backdrop-blur-md sticky top-0 z-10">
                <div className="flex items-center gap-2">
                  <FilterIcon size={15} className="text-emerald-700" />
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("filters")}</div>
                </div>
                <button
                  data-testid="filters-close"
                  onClick={() => setFiltersOpen(false)}
                  className="w-7 h-7 rounded-full text-slate-500 hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center"
                  aria-label={t("close")}
                >
                  <X size={14} />
                </button>
              </div>
              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-3 py-2.5 space-y-2.5">
              {/* Content-type picker: reports + 3 marketplace flavors. Each toggle
                  triggers a real backend request and updates markers, list and count. */}
              <div className="p-2 rounded-xl border border-slate-200 bg-white" data-testid="filter-content-types">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Tipologia contenuti</div>
                <label className="flex items-center gap-2 py-1 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="filter-type-reports"
                    checked={showReports}
                    onChange={(e) => setShowReports(e.target.checked)}
                    className="w-4 h-4 rounded accent-emerald-600 shrink-0"
                  />
                  <Leaf size={13} className="text-emerald-700" />
                  <span className="text-xs font-semibold text-slate-700 flex-1">Segnalazioni ambientali</span>
                  {showReports && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">{visibleReports.length}</span>}
                </label>
                {[
                  { k: "sale", lb: "Oggetti in vendita", color: "#7C3AED", icon: Tag },
                  { k: "gift", lb: "Oggetti da regalare", color: "#2563EB", icon: Gift },
                  { k: "reusable", lb: "Oggetti riutilizzabili trovati", color: "#8B5E34", icon: Recycle },
                ].map((it) => {
                  const Icon = it.icon;
                  const active = mkTypes.has(it.k);
                  const n = visibleMkItems.filter((x) => x.type === it.k).length;
                  return (
                    <label key={it.k} className="flex items-center gap-2 py-1 cursor-pointer" data-testid={`filter-type-${it.k}-wrap`}>
                      <input
                        type="checkbox"
                        data-testid={`filter-type-${it.k}`}
                        checked={active}
                        onChange={() => toggleMkType(it.k)}
                        className="w-4 h-4 rounded shrink-0"
                        style={{ accentColor: it.color }}
                      />
                      <Icon size={13} style={{ color: it.color }} />
                      <span className="text-xs font-semibold text-slate-700 flex-1">{it.lb}</span>
                      {active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold text-white" style={{ background: it.color }}>{n}</span>
                      )}
                    </label>
                  );
                })}
              </div>

              {/* Urgent alerts toggle — first so it never covers other filters below */}
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-xl bg-red-50 border border-red-200" data-testid="filter-urgent-toggle-wrap">
                <input
                  type="checkbox"
                  data-testid="filter-urgent-toggle"
                  checked={showUrgent}
                  onChange={(e) => setShowUrgent(e.target.checked)}
                  className="w-4 h-4 rounded accent-red-500 shrink-0"
                />
                <span className={`text-xs font-semibold ${showUrgent ? "text-red-700" : "text-slate-700"} min-w-0 break-words flex-1`}>{t("filter_urgent_alerts")}</span>
                {showUrgent && urgentAlerts.length > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-600 text-white font-bold shrink-0">{urgentAlerts.length}</span>
                )}
              </label>

              {/* Empty-state message when the user has deselected every content type. */}
              {!showReports && mkTypes.size === 0 && (
                <div className="p-3 rounded-xl border-2 border-dashed border-slate-300 bg-white text-center text-xs text-slate-600" data-testid="filter-empty-hint">
                  Seleziona almeno una tipologia di contenuto
                </div>
              )}

              {/* 3-4-5. Category / Subcategory / Item type
                  Two disjoint sections depending on which content type is active.
                  Environmental reports and marketplace listings have different taxonomies,
                  so we render dedicated cascades one under the other. */}
              {showReports && (
                <div className="border-t border-slate-200 pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("category")} · Segnalazioni</div>
                  <Select
                    value={filters.category}
                    onValueChange={(v) => setFilters({ ...filters, category: v, subcategory: "all" })}
                  >
                    <SelectTrigger data-testid="filter-category" className="h-9 text-sm rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("all")}</SelectItem>
                      <SelectItem value="waste">{t("cat_waste")}</SelectItem>
                      <SelectItem value="environment">{t("cat_environment")}</SelectItem>
                      <SelectItem value="animals">{t("cat_animals")}</SelectItem>
                      <SelectItem value="urban">{t("cat_urban")}</SelectItem>
                      <SelectItem value="vegetation">{t("cat_vegetation")}</SelectItem>
                    </SelectContent>
                  </Select>
                  {filters.category !== "all" && (
                    <div className="mt-2">
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("subcategory")}</div>
                      <Select value={filters.subcategory} onValueChange={(v) => setFilters({ ...filters, subcategory: v })}>
                        <SelectTrigger data-testid="filter-subcategory" className="h-9 text-sm rounded-lg"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-64">
                          <SelectItem value="all">{t("all_subcategories")}</SelectItem>
                          {availableSubs.map((s) => (
                            <SelectItem key={s} value={s} className="capitalize">{t(`sub_${s}`) || s.replace(/_/g," ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {mkTypes.size > 0 && (
                <div className={`border-t border-slate-200 pt-3`}>
                  {/* Cerca per parola — same behaviour as Marketplace feed
                      (title, description, keywords, category, subcategory,
                      item_type, provincia). Debounced 300ms. */}
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("search")}</div>
                  <div className="relative mb-2">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      data-testid="filter-mk-query"
                      value={mkQuery}
                      onChange={(e) => setMkQuery(e.target.value)}
                      placeholder={t("search_map_placeholder")}
                      className="w-full h-9 text-sm rounded-lg border border-slate-200 bg-white pl-9 pr-9 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                    {mkQuery && (
                      <button
                        type="button"
                        data-testid="filter-mk-query-clear"
                        onClick={() => setMkQuery("")}
                        aria-label={t("clear_search")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("category")} · Marketplace</div>
                  <Select value={mkCat} onValueChange={setMkCat}>
                    <SelectTrigger data-testid="filter-mk-cat" className="h-9 text-sm rounded-lg"><SelectValue placeholder={t("all_categories")} /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="all">{t("all_categories")}</SelectItem>
                      {Object.keys(mkTaxonomy).map((c) => (
                        <SelectItem key={c} value={c} data-testid={`filter-mk-cat-opt-${c}`}>{labelCat(c, lang)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("subcategory")}</div>
                    <Select value={mkSub} onValueChange={setMkSub} disabled={mkCat === "all"}>
                      <SelectTrigger data-testid="filter-mk-sub" className="h-9 text-sm rounded-lg"><SelectValue placeholder={mkCat === "all" ? t("category_first") : t("all_subcategories")} /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="all">{t("all_subcategories")}</SelectItem>
                        {mkCat !== "all" && Object.keys(mkTaxonomy[mkCat] || {}).map((s) => (
                          <SelectItem key={s} value={s} data-testid={`filter-mk-sub-opt-${s}`}>{labelSub(s, lang)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="mt-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("type_specific_label")}</div>
                    <Select value={mkItem} onValueChange={setMkItem} disabled={mkSub === "all"}>
                      <SelectTrigger data-testid="filter-mk-item" className="h-9 text-sm rounded-lg"><SelectValue placeholder={mkSub === "all" ? t("subcategory_first") : t("all_types")} /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="all">{t("all_types")}</SelectItem>
                        {mkSub !== "all" && (mkTaxonomy?.[mkCat]?.[mkSub] || []).map((i) => (
                          <SelectItem key={i} value={i} data-testid={`filter-mk-item-opt-${i}`}>{labelItem(i, lang)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* 6. Prezzo + condizione (solo Vendo). Applicati solo agli annunci sale;
                     gli annunci gift/reusable non vengono esclusi anche quando presenti. */}
              {mkTypes.has("sale") && (
                <div className="border-t border-slate-200 pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{t("sale_price_only")}</div>
                  <div className="grid grid-cols-2 gap-2" data-testid="filter-mk-price">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Min €</label>
                      <Input data-testid="filter-mk-price-min" type="number" inputMode="decimal" value={mkPriceMin}
                             onChange={(e) => setMkPriceMin(e.target.value)} placeholder="0" className="h-8 rounded-lg mt-0.5" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Max €</label>
                      <Input data-testid="filter-mk-price-max" type="number" inputMode="decimal" value={mkPriceMax}
                             onChange={(e) => setMkPriceMax(e.target.value)} placeholder="—" className="h-8 rounded-lg mt-0.5" />
                    </div>
                    {mkPriceMin && mkPriceMax && Number(mkPriceMin) > Number(mkPriceMax) && (
                      <div className="col-span-2 text-[10px] text-amber-700 bg-amber-50 rounded-lg px-2 py-1" data-testid="filter-mk-price-warn">
                        Il minimo è maggiore del massimo: applichiamo comunque l'intervallo corretto.
                      </div>
                    )}
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 items-end">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Condizione</label>
                      <Select value={mkCondition} onValueChange={setMkCondition}>
                        <SelectTrigger data-testid="filter-mk-condition" className="h-8 text-xs rounded-lg mt-0.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte</SelectItem>
                          <SelectItem value="new">Nuovo</SelectItem>
                          <SelectItem value="like_new">Come nuovo</SelectItem>
                          <SelectItem value="good">Buono</SelectItem>
                          <SelectItem value="used">Usato</SelectItem>
                          <SelectItem value="fair">Discreto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <label className="text-xs font-semibold flex items-center gap-2 h-8">
                      <input type="checkbox" data-testid="filter-mk-negotiable" checked={mkNegotiable}
                             onChange={(e) => setMkNegotiable(e.target.checked)} className="w-4 h-4 rounded accent-purple-600" />
                      Trattabile
                    </label>
                  </div>
                </div>
              )}

              {/* 7. Provincia / area amministrativa — mostrata solo se c'è almeno una tipologia selezionata. */}
              {(showReports || mkTypes.size > 0) && (
                <div className="border-t border-slate-200 pt-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{t("filter_area")}</div>
                  <GeoSearchAutocomplete value={filters.area} onChange={(area) => setFilters({ ...filters, area })} testid="filter-area" placeholder={t("filter_area")} />
                  {filters.area && (
                    <div className="mt-1 text-[10px] text-emerald-800 font-semibold" data-testid="filter-area-active">
                      {t("active_prefix")} {filters.area.display}
                    </div>
                  )}
                </div>
              )}

              {/* 8. Vicino a me — geolocation + radius. Universal filter. */}
              {(showReports || mkTypes.size > 0) && (
                <div className="p-2 rounded-xl bg-emerald-50 border border-emerald-200" data-testid="filter-proximity">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid="filter-prox-toggle"
                      onClick={askProximity}
                      className={`h-8 px-3 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${proxOn ? "bg-emerald-600 text-white" : "bg-white text-slate-700 border border-emerald-200"}`}
                    >
                      <LocateFixed size={13} /> {geoBusy ? "Localizzazione…" : proxOn ? "Attivo" : "Vicino a me"}
                    </button>
                    {proxOn && meLoc && (
                      <>
                        <Select value={String(radiusKm)} onValueChange={(v) => setRadiusKm(Number(v))}>
                          <SelectTrigger data-testid="filter-prox-radius" className="h-8 rounded-full flex-1 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {[1, 5, 10, 25, 50, 100, 250].map((v) => <SelectItem key={v} value={String(v)}>{v} km</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <button type="button" data-testid="filter-prox-clear" onClick={() => setProxOn(false)}
                                className="w-7 h-7 rounded-full text-slate-500 hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center" title="Rimuovi filtro prossimità">
                          <X size={13} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* 9. Colore + Stato — solo per le Segnalazioni ambientali. */}
              {showReports && (
                <>
                  <div className="border-t border-slate-200 pt-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{t("color")} · Segnalazioni</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="filter-colors">
                      {[
                        { k: "green", label: t("green"), bg: "#10B981" },
                        { k: "yellow", label: t("yellow"), bg: "#EAB308" },
                        { k: "red", label: t("red"), bg: "#EF4444" },
                      ].map((c) => (
                        <button
                          key={c.k}
                          data-testid={`filter-color-${c.k}`}
                          onClick={() => toggleInArr(risks, c.k, setRisks)}
                          className={`flex-1 min-w-[70px] h-9 rounded-full text-xs font-semibold border-2 inline-flex items-center justify-center gap-1 ${risks.includes(c.k) ? "text-white" : "text-slate-700 bg-white hover:bg-slate-50"}`}
                          style={risks.includes(c.k) ? { background: c.bg, borderColor: c.bg } : { borderColor: "#E2E8F0" }}
                        >
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.bg }} /> {c.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">{t("status")} · Segnalazioni</div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          data-testid="filter-status-trigger"
                          className="w-full h-9 rounded-lg border border-slate-200 bg-white px-3 flex items-center justify-between text-sm text-slate-800 hover:bg-slate-50"
                        >
                          <span className="truncate" data-testid="filter-status-label">
                            {statuses.length === 0 ? t("map_status_all") : statuses.length === 1
                              ? ({published: t("st_active"), in_progress: t("st_taken"), in_review: t("st_review"), standby: t("st_standby"), resolved: t("st_resolved"), reopened: t("st_reopened")}[statuses[0]] || statuses[0])
                              : t("map_status_selected").replace("{n}", String(statuses.length))
                            }
                          </span>
                          <ChevronDown size={14} className="text-slate-500 shrink-0" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="w-56 z-[1100]" data-testid="filter-statuses">
                        <DropdownMenuCheckboxItem data-testid="filter-status-all" checked={statuses.length === 0} onCheckedChange={(v) => { if (v) setStatuses([]); }}>
                          {t("map_status_all")}
                        </DropdownMenuCheckboxItem>
                        {[
                          { v: "published", l: t("st_active") },
                          { v: "in_progress", l: t("st_taken"), dot: "#3B82F6" },
                          { v: "in_review", l: t("st_review") },
                          { v: "standby", l: t("st_standby") },
                          { v: "resolved", l: t("st_resolved"), dot: "#9CA3AF" },
                          { v: "reopened", l: t("st_reopened") },
                        ].map((s) => (
                          <DropdownMenuCheckboxItem key={s.v} data-testid={`filter-status-${s.v}`} checked={statuses.includes(s.v)} onCheckedChange={() => toggleInArr(statuses, s.v, setStatuses)}>
                            {s.dot && <span className="w-2 h-2 rounded-full shrink-0 mr-2" style={{ background: s.dot }} />}
                            {s.l}
                          </DropdownMenuCheckboxItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </>
              )}
            </div>
            {/* Sticky footer with Applica + Azzera actions always reachable */}
            <div className="px-3 py-2 border-t border-slate-200/70 bg-white/85 backdrop-blur-md rounded-b-2xl sticky bottom-0 z-10 flex gap-2">
              <Button
                data-testid="filters-clear"
                size="sm"
                variant="outline"
                onClick={clearAll}
                className="flex-1 h-9 rounded-full text-xs font-semibold hover:bg-slate-900 hover:text-white"
              >
                <RotateCcw size={12} className="mr-1" /> {t("reset")}
              </Button>
              <Button
                data-testid="filters-apply"
                size="sm"
                onClick={() => setFiltersOpen(false)}
                className="flex-1 h-9 rounded-full text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {t("apply") || "Applica"}
              </Button>
            </div>
          </div>
          </>
        )}

        {/* Standalone floating Legend — always visible, never covered by FAB "Nuova segnalazione"
            or "Evento straordinario". Hidden while the filter panel is open to avoid covering Applica/Azzera. */}
        {/* Legenda pill removed on user request. */}

        {/* Counter + list panel — reflects reports AND urgent alerts matching current filters,
            so map/list/counter/statistics stay perfectly in sync. */}
        <div className="absolute left-4 top-16 z-[999] pointer-events-none" style={{ marginLeft: filtersOpen ? 316 : 0 }}>
          <div className="pointer-events-auto flex flex-col items-start gap-2">
            <button
              data-testid="map-count-chip"
              onClick={() => setListOpen((v) => !v)}
              className="h-10 rounded-full glass-panel px-4 flex items-center gap-2 text-sm font-semibold text-slate-800 hover:bg-white"
              title={t("map_reports_list_title")}
            >
              <ListIcon size={14} className="text-emerald-700" />
              <span data-testid="map-count-value" className="tabular-nums">{loading ? "…" : (visibleReports.length + (showUrgent ? visibleAlerts.length : 0) + visibleMkItems.length)}</span>
              <span className="text-slate-500 font-normal hidden sm:inline">
                {proxOn && meLoc
                  ? t("map_within_km").replace("{km}", String(radiusKm))
                  : (filters.area?.name
                      ? t("map_within_area").replace("{area}", filters.area.name)
                      : (mkTypes.size > 0 ? t("map_count_results") : t("map_count_reports")))}
              </span>
              {filters.area?.name && (
                <span
                  data-testid="map-count-clear"
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setFilters({ ...filters, area: null }); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setFilters({ ...filters, area: null }); } }}
                  className="ml-1 h-5 w-5 inline-flex items-center justify-center rounded-full text-slate-500 hover:text-red-600 hover:bg-red-50 cursor-pointer"
                  title={t("map_clear_area_filter")}
                >
                  <X size={12} />
                </span>
              )}
              {listOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {listOpen && (
              <div className="w-[280px] max-h-[55vh] overflow-y-auto glass-panel rounded-2xl p-2 fade-in" data-testid="map-list-panel">
                {(visibleReports.length + (showUrgent ? visibleAlerts.length : 0) + visibleMkItems.length) === 0 && (
                  <div className="p-4 text-center text-xs text-slate-500" data-testid="map-list-empty">{t("no_reports_for_filters")}</div>
                )}
                {showUrgent && visibleAlerts.map((e) => (
                  <Link
                    key={e.event_id}
                    to={`/events/${e.event_id}`}
                    data-testid={`map-list-event-${e.event_id}`}
                    className="flex items-start gap-2 p-2 rounded-xl hover:bg-red-50/60"
                  >
                    <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 bg-red-500" />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-red-800 truncate">⚠ {e.title || (t(`event_kind_${e.kind}`) || e.kind)}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {t("extraordinary_event") || "Evento straordinario"} · {t(`event_kind_${e.kind}`) || e.kind}
                        {e.province_name ? ` · ${e.province_name}` : ""}
                      </div>
                    </div>
                  </Link>
                ))}
                {visibleReports.map((r) => (
                  <Link
                    key={r.report_id}
                    to={`/report/${r.report_id}`}
                    data-testid={`map-list-item-${r.report_id}`}
                    className="flex items-start gap-2 p-2 rounded-xl hover:bg-emerald-50/60"
                  >
                    <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: RISK_COLORS[r.risk_level] || "#9CA3AF" }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-900 truncate">{r.description || catLabel(r.category)}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {catLabel(r.category)}{r.subcategory ? ` · ${(t(`sub_${r.subcategory}`) || r.subcategory).replace(/_/g," ")}` : ""}
                        {r.province_name ? ` · ${r.province_name}` : ""}
                      </div>
                    </div>
                  </Link>
                ))}
                {visibleMkItems.map((l) => (
                  <Link
                    key={`mk-${l.listing_id}`}
                    to={`/marketplace/${l.listing_id}`}
                    data-testid={`map-list-mk-${l.listing_id}`}
                    className="flex items-start gap-2 p-2 rounded-xl hover:bg-emerald-50/60"
                  >
                    <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ background: MK_TYPE_COLOR[l.type] }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-900 truncate">
                        {l.title}
                        {l.type === "sale" && l.price != null && <span className="ml-1 text-purple-700 font-bold">€ {l.price}</span>}
                      </div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {MK_TYPE_LABEL[l.type]}{l.province_name ? ` · ${l.province_name}` : ""}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* FAB: minimizable. On mobile the group is centered horizontally so it does
            not cover the Leaflet attribution (bottom-right) and to give a more balanced
            look. On desktop we keep it right-aligned. */}
        {fabMinimized ? (
          <button
            data-testid="fab-expand"
            onClick={() => setFabMinimized(false)}
            className="absolute left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-4 z-[1000] w-12 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform bottom-[calc(1rem+env(safe-area-inset-bottom,0px))]"
            title={t("fab_expand")}
          >
            <ChevronUp size={20} />
          </button>
        ) : (
          <div className="absolute left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-4 z-[1000] flex items-center gap-2 max-w-[calc(100vw-32px)] bottom-[calc(1rem+env(safe-area-inset-bottom,0px))]">
            <button
              data-testid="fab-event"
              onClick={() => user ? nav("/events/new") : openGate({ message: t("guest_gate_action") })}
              className="h-12 w-12 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
              title={t("fab_event") || "Nuovo evento straordinario"}
            >
              <AlertTriangle size={20} />
            </button>
            <button
              data-testid="fab-new-report"
              onClick={() => user ? setShowChooser(true) : openGate({ message: t("guest_gate_action") })}
              className="h-12 pl-4 pr-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xl flex items-center gap-2 font-semibold hover:scale-[1.02] transition-transform"
              title={t("admin_publish_new_report")}
            >
              <Plus size={18} /> <span className="whitespace-nowrap text-sm sm:text-base">{t("publish")}</span>
            </button>
            <button
              data-testid="fab-minimize"
              onClick={() => setFabMinimized(true)}
              className="w-10 h-10 rounded-full glass-panel flex items-center justify-center hover:bg-white"
              title={t("fab_minimize")}
            >
              <ChevronDown size={16} className="text-slate-800" />
            </button>
          </div>
        )}

        {loading && <div className="absolute inset-0 flex items-center justify-center bg-white/40 backdrop-blur-sm z-[999] pointer-events-none">
          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </div>}
        </div>
      </div>
    </div>
  );
}

/**
 * Floating map legend — bottom-CENTER on desktop, above bottom nav / FAB on mobile.
 * Collapsible: closed = small pill; open = compact 6-item legend.
 * Positioned to NEVER overlap: FAB (bottom-right), Locate (bottom-24 right-4), ZoomControl (bottomleft).
 */
// FloatingLegend removed on user request (iter45): the pill was cluttering
// mobile screens and duplicating information already conveyed by the pins.
